import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { crmAPI, trekAPI } from '../services/api';
import { isValidPhoneInput, normalizePhoneInput, phoneInputProps } from '../utils/phoneInput.js';

const EMPTY_FORM = {
  name: '',
  phone: '',
  trekId: '',
  reason: '',
  comment: '',
  assignedTo: ''
};

export default function CreateSupportTicketDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { data: reasonsRes } = useQuery(['crm-reasons'], () => crmAPI.getReasons(), { enabled: open });
  const reasons = reasonsRes?.data?.data || [];

  const { data: assigneesRes, isLoading: assigneesLoading } = useQuery(
    ['crm-assignees'],
    () => crmAPI.getAssignees(),
    { enabled: open }
  );
  const assignees = assigneesRes?.data?.data || [];

  const { data: treksRes } = useQuery(
    ['crm-support-ticket-treks'],
    () => trekAPI.list({ page: 1, limit: 500, isActive: true }),
    { enabled: open }
  );

  const treks = useMemo(() => {
    const rows = treksRes?.data?.data || [];
    return [...rows].sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    );
  }, [treksRes]);

  const selectedTrek = treks.find((t) => String(t.id) === String(form.trekId)) ?? null;
  const selectedAssignee = assignees.find((u) => String(u.id) === String(form.assignedTo)) ?? null;

  const reasonOptions = useMemo(() => {
    const cur = String(form.reason || '').trim();
    if (!cur || reasons.includes(cur)) return reasons;
    return [...reasons, cur];
  }, [reasons, form.reason]);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setError('');
    setSaving(false);
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!isValidPhoneInput(form.phone)) {
      setError('Enter a valid 10-digit mobile number starting with 6–9.');
      return;
    }
    if (!form.assignedTo) {
      setError('Select a user to assign this ticket.');
      return;
    }

    setSaving(true);
    try {
      const trek = selectedTrek;
      await crmAPI.createSupportTicket({
        name: form.name.trim(),
        phone: normalizePhoneInput(form.phone),
        reason: form.reason.trim() || undefined,
        comment: form.comment.trim() || undefined,
        assignedTo: Number(form.assignedTo),
        trekId: trek?.id ?? undefined,
        eventSlug: trek?.slug ?? undefined,
        eventTitle: trek?.name ?? undefined
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not create ticket.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Create support ticket</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ pt: 2.5, pb: 2.5 }}>
          {error ? (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
          ) : null}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Name"
              required
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              autoFocus
            />

            <TextField
              fullWidth
              size="small"
              label="Phone"
              required
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: normalizePhoneInput(e.target.value) }))}
              inputProps={phoneInputProps}
              helperText="10-digit mobile starting with 6–9"
            />

            <Autocomplete
              size="small"
              options={treks}
              getOptionLabel={(t) => t?.name ?? ''}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              value={selectedTrek}
              onChange={(_, trek) =>
                setForm((p) => ({ ...p, trekId: trek ? String(trek.id) : '' }))
              }
              noOptionsText="No treks found"
              ListboxProps={{ style: { maxHeight: 280 } }}
              renderInput={(params) => (
                <TextField {...params} label="Trek (optional)" placeholder="Search trek…" />
              )}
            />

            <FormControl fullWidth size="small">
              <InputLabel id="support-ticket-reason-label" shrink>
                Reason
              </InputLabel>
              <Select
                labelId="support-ticket-reason-label"
                label="Reason"
                displayEmpty
                notched
                value={form.reason}
                onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                renderValue={(selected) =>
                  selected ? selected : <em style={{ color: '#94a3b8' }}>—</em>
                }
              >
                <MenuItem value="">—</MenuItem>
                {reasonOptions.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="Comment (optional)"
              multiline
              rows={2}
              value={form.comment}
              onChange={(e) => setForm((p) => ({ ...p, comment: e.target.value }))}
            />

            <Autocomplete
              size="small"
              options={assignees}
              loading={assigneesLoading}
              getOptionLabel={(u) => u?.name ?? ''}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              value={selectedAssignee}
              onChange={(_, user) =>
                setForm((p) => ({ ...p, assignedTo: user ? String(user.id) : '' }))
              }
              noOptionsText={assigneesLoading ? 'Loading…' : 'No staff users found'}
              ListboxProps={{ style: { maxHeight: 280 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign to"
                  required
                  placeholder="Search staff user…"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {assigneesLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={onClose} disabled={saving} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Creating…' : 'Create ticket'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
